import { useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { Dialog, SwipeableViews } from '@cast/design-system';
import { Cluster } from '@cast/types';
import { useScheduledJob, isE2E } from '@cast/utils';

import { ErrorSlide } from './_components/ErrorSlide';
import { InstallingSlide } from './_components/InstallingSlide';
import { RunTheScriptSlide } from './_components/RunTheScriptSlide';
import { SuccessSlide } from './_components/SuccessSlide';
import { useWasGpuExporterInstalled } from '../../_hooks/useWasGpuExporterInstalled';

import { ClusterProvider } from '@cast/shared-cluster-context-data-access';

type Props = {
  cluster: Cluster;
  destroy: () => void;
  onEnabled?: () => void;
};
const useInstallTimeout = (
  isConnecting: boolean,
  onTimeout: () => void,
  timeout = 60 * 3 * 1000
) => {
  const { start, clear } = useScheduledJob();
  useEffect(() => {
    if (isConnecting) {
      start({
        when: dayjs().add(timeout, 'milliseconds'),
        job: () => onTimeout(),
      });
    }
    return clear;
  }, [clear, isConnecting, onTimeout, start, timeout]);
};

export const InstallGpuExporterDialog = ({
  destroy,
  cluster,
  onEnabled: _onEnabled,
}: Props) => {
  const [activeSlide, setActiveSlide] = useState<
    'runTheScript' | 'installing' | 'error' | 'success'
  >('runTheScript');

  const isConnecting = activeSlide === 'installing';

  const onError = useCallback(() => {
    setActiveSlide('error');
  }, []);

  useInstallTimeout(isConnecting, onError);

  const onInstalled = useCallback(() => {
    setActiveSlide('success');
    _onEnabled?.();
  }, [_onEnabled]);

  useWasGpuExporterInstalled({
    clusterId: cluster.id,
    onSuccess: onInstalled,
    onError,
    enabled: isConnecting,
  });

  return (
    <Dialog
      open
      onClose={destroy}
      disableCloseOnBackdropClick
      testId="install-gpu-exporter-dialog"
    >
      <ClusterProvider cluster={cluster}>
        <SwipeableViews
          slides={{
            runTheScript: (
              <RunTheScriptSlide
                onClose={destroy}
                clusterId={cluster.id}
                onRunScriptClick={() => {
                  setActiveSlide('installing');
                }}
              />
            ),
            error: <ErrorSlide onClose={destroy} />,
            success: <SuccessSlide onClose={destroy} />,
            installing: <InstallingSlide onClose={destroy} />,
          }}
          disableAnimations={isE2E()}
          activeSlide={activeSlide}
          onSlideChange={setActiveSlide}
        />
      </ClusterProvider>
    </Dialog>
  );
};
